import useSearchObjects from "backend/hooks/elements/useSearchObjects";
import { ICardElementWithChildren } from "backend/model/model";
import axios from "axios";
import SearchBarDesign from "design/components/search/SearchBar";
import SearchResultRow from "design/components/search/SearchResultRow";
import * as conf from "qpr-base/configuration";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useCurrentUser from "qpr-base/context/UserContext";
import useLink from "qpr-base/hooks/useLink";
import React from "react";
import typeToView from "routing/typetoview";
import { useSearchResults } from "./useSearchResults";

const SearchContainer = () => {
  const link = useLink();
  const { modelId, fullModelId, period, language } = useAppContext();
  const user = useCurrentUser();
  const isTenantAdmin = user?.profile?.tenantAdmin === true;
  const hasAdminRole = Boolean(
    user?.profile?.role?.toLowerCase?.().includes("admin") ||
      user?.profile?.uiRole?.toLowerCase?.().includes("admin") ||
      user?.profile?.roles?.some((r) => r?.toLowerCase?.().includes("admin"))
  );
  const canSelectDivision = isTenantAdmin || hasAdminRole;
  const extractYear = React.useCallback((periodName?: string): string => {
    if (!periodName) return "";
    const slashYearMatch = periodName.match(/\/\s*(\d{4})/);
    if (slashYearMatch?.[1]) return slashYearMatch[1];
    const yearAtEndMatch = periodName.match(/(20\d{2})$/);
    return yearAtEndMatch?.[1] || "";
  }, []);
  const parsePeriod = React.useCallback((periodName?: string) => {
    const safe = String(periodName || "");
    const slashMatch = safe.match(/(\d{1,2})\s*\/\s*(\d{4})/);
    if (slashMatch) {
      return { raw: safe, year: Number(slashMatch[2]), subPeriod: Number(slashMatch[1]) };
    }
    const year = Number(extractYear(safe));
    return { raw: safe, year: Number.isFinite(year) ? year : 0, subPeriod: 0 };
  }, [extractYear]);
  const [selectedYear, setSelectedYear] = React.useState(extractYear(period || ""));
  const [selectedDivisionId, setSelectedDivisionId] = React.useState("");
  const [periodOptions, setPeriodOptions] = React.useState<string[]>([]);
  const [yearToPeriod, setYearToPeriod] = React.useState<Record<string, string>>({});
  const [divisionOptions, setDivisionOptions] = React.useState<
    { id: string; name: string }[]
  >([]);

  const selectedPeriod = React.useMemo(
    () => yearToPeriod[selectedYear] || period || "",
    [yearToPeriod, selectedYear, period]
  );

  const { data, isLoading } = useSearchObjects(
    ["KPI", "Initiative", "Strategic Objective"],
    selectedPeriod || period
  );
  const [searchCandidate, setSearchCandidate] = React.useState("");
  React.useEffect(() => {
    setSelectedYear(extractYear(period || ""));
  }, [period, extractYear]);
  React.useEffect(() => {
    if (!fullModelId) return;
    let mounted = true;
    const fetchPeriods = async () => {
      try {
        const response = await axios.post("meaquery", {
          query: `[${fullModelId}].periods`,
          attributes: "name",
          sortBy: "name",
          jsonmode: 1,
        });
        const rows = response?.data?.data;
        if (!mounted || !Array.isArray(rows)) return;
        const uniquePeriods = Array.from(
          new Set(rows.map((r: any) => r?.name).filter(Boolean))
        ).map((n) => String(n));
        const sortedPeriods = [...uniquePeriods].sort((a, b) => {
          const pa = parsePeriod(a);
          const pb = parsePeriod(b);
          if (pa.year !== pb.year) return pb.year - pa.year;
          if (pa.subPeriod !== pb.subPeriod) return pb.subPeriod - pa.subPeriod;
          return pb.raw.localeCompare(pa.raw);
        });
        const map: Record<string, string> = {};
        sortedPeriods.forEach((periodName) => {
          const year = extractYear(periodName);
          if (year && !map[year]) map[year] = periodName;
        });
        const years = Object.keys(map).sort((a, b) => Number(b) - Number(a));
        setYearToPeriod(map);
        setPeriodOptions(years);
        if (years.length > 0 && !selectedYear) {
          setSelectedYear(years[0]);
        }
      } catch {
        if (mounted) {
          const fallbackYear = extractYear(period || "");
          setYearToPeriod(fallbackYear ? { [fallbackYear]: period || "" } : {});
          setPeriodOptions(fallbackYear ? [fallbackYear] : []);
        }
      }
    };
    fetchPeriods();
    return () => {
      mounted = false;
    };
  }, [fullModelId, period, extractYear, parsePeriod, selectedYear]);
  React.useEffect(() => {
    if (!canSelectDivision || !modelId) return;
    let mounted = true;
    const fetchDivisions = async () => {
      try {
        const response = await axios.get(`/workflow/topelements/SC.${modelId}`);
        const rows = response?.data?.data;
        if (!mounted || !Array.isArray(rows)) return;
        const mapped = rows.map((row: any) => ({
          id: row.id,
          name:
            row?.JS_ATTR?.[language]?.Name ||
            row?.translations?.[language]?.Name ||
            row?.name ||
            row?.symbol ||
            row?.id,
        }));
        setDivisionOptions(mapped);
      } catch {
        if (mounted) setDivisionOptions([]);
      }
    };
    fetchDivisions();
    return () => {
      mounted = false;
    };
  }, [canSelectDivision, modelId, language]);

  const { results, groupedResults } = useSearchResults(
    (selectedDivisionId
      ? (data as ICardElementWithChildren[] | undefined)?.filter(
          (elem) =>
            (elem as any)?.top_element_id === selectedDivisionId ||
            (elem as any)?.topelementid === selectedDivisionId
        )
      : data) as ICardElementWithChildren[] | undefined,
    isLoading,
    searchCandidate,
    modelId
  );

  const getRangeColor = (element: ICardElementWithChildren): string | undefined => {
    const settings =
      element.typename === "KPI"
        ? conf.getKPIsViewGaugeSettings(element)
        : conf.getInitiativeDetailsViewGaugeSettings(element);
    if (!!settings.colorAttribute)
      return element[settings.colorAttribute as keyof ICardElementWithChildren] as string;
    return element["rangecolor"];
  };

  return (
    <div>
      <SearchBarDesign
        maxResults={1000}
        results={results}
        groupedResults={groupedResults}
        periodOptions={
          periodOptions.length > 0
            ? periodOptions
            : [extractYear(selectedPeriod)].filter(Boolean)
        }
        selectedPeriod={selectedYear}
        onPeriodChange={setSelectedYear}
        canSelectDivision={canSelectDivision}
        divisionOptions={divisionOptions}
        selectedDivisionId={selectedDivisionId}
        onDivisionChange={setSelectedDivisionId}
        RenderRow={(props) => {
          const item = { ...props.value };
          if (item.typename === "KPI") {
            item.PERF_prettyvalue = item.PERF_YTD_prettyvalue;
            item.rangecolor = getRangeColor(item);
          }
          if (item.typename === "Initiative") {
            item.PERF_prettyvalue = item.COMPLETION_PERCENTAGE_prettyvalue;
            item.rangecolor = getRangeColor(item);
          }
          return <SearchResultRow value={item} onNavigate={props.onNavigate} />;
        }}
        isLoading={isLoading}
        onNavigate={(value) => {
          const page = typeToView(value.typename);
          if (!page) return;
          link({
            page,
            meaId: value.id,
          });
        }}
        onSearch={(value) => {
          setSearchCandidate(value);
        }}
      />
    </div>
  );
};

export default SearchContainer;

